import { AttachmentFile, Task } from '@nextforce/api'
import { Button, Icon, Image, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import {
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  View,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ScrollView } from 'react-native-gesture-handler'
import ParsedText, { ParsedTextProps } from 'react-native-parsed-text'
import { AttachmentPreview } from 'shared/components/chat/attachment_preview'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { getDarkShadeColor } from 'shared/utils'
import { UpdateTypeChooser } from 'tasks/screens/edit_task/views'
import { RecurrentTaskUpdateType } from 'tasks/types'
import { getTaskStatus } from 'tasks/utils'

const PHOTO_PREVIEW_SIZE = 110
const ROW_ITEM_VALUE_HEIGHT = 60

interface TaskDetailsViewProps {
  task: Task | undefined
  onCompleteTask: () => Promise<void>
  onPressRemovePhoto: (photo: AttachmentFile) => void
  onPressEditPhoto: (photo: AttachmentFile) => void
  photos: AttachmentFile[]
  onPressAddPhoto: () => void
  onDeleteTask: (updateType?: RecurrentTaskUpdateType) => Promise<void>
  onReassignTask: () => void
  isApprover: boolean
  onPressPhoto: (url: string) => void
  isAssignee: boolean
  onLinkPress: (text: string) => void
}
export function TaskDetailsView({
  task,
  onCompleteTask,
  onPressRemovePhoto,
  onPressEditPhoto,
  photos,
  onPressAddPhoto,
  onDeleteTask,
  onReassignTask,
  isApprover,
  isAssignee,
  onPressPhoto,
  onLinkPress,
}: TaskDetailsViewProps) {
  const styles = useStyles()
  const occurrence = task?.occurrences[0]
  const taskStatus = getTaskStatus(task)
  const [isLoading, setIsLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const { theme } = useTheme()
  const isCompleted = occurrence?.completed ?? false
  const isSubmitDisabled = useMemo(() => {
    const isPhotoRequired = occurrence?.photoRequired
    if (isPhotoRequired) {
      return photos.length === 0
    }
  }, [occurrence?.photoRequired, photos])
  const canUploadPhotos = isAssignee && !isCompleted

  const parsePatterns: ParsedTextProps['parse'] = useMemo(() => {
    const patterns: ParsedTextProps['parse'] = [
      {
        style: { textDecorationLine: 'underline', color: theme.colors.primary },
        pattern: /(https?:\/\/|www\.)[^\s\]]+/gi,
        onPress: (text: string) => {
          onLinkPress(text)
        },
      },
    ]
    return patterns
  }, [onLinkPress, theme.colors.primary])

  const submittedPhotos: AttachmentFile[] = useMemo(() => {
    return (
      occurrence?.urls?.map(url => ({
        uri: url,
        name: url,
        mimeType: 'image/jpeg',
        type: 'media',
      })) ?? []
    )
  }, [occurrence?.urls])

  const onPressCompleteTask = useCallback(async () => {
    setIsLoading(true)
    await onCompleteTask()
    setIsLoading(false)
  }, [onCompleteTask])

  const onPressDeleteTask = useCallback(async () => {
    if (task?.recurring) {
      setModalVisible(true)
    } else {
      setIsLoading(true)
      await onDeleteTask()
    }
    setIsLoading(false)
  }, [onDeleteTask, task?.recurring])

  const onConfirmDeleteRecurringTask = useCallback(
    async (updateType: RecurrentTaskUpdateType) => {
      setIsLoading(true)
      setModalVisible(false)
      await onDeleteTask(updateType)
      setIsLoading(false)
    },
    [onDeleteTask]
  )

  const onCloseModal = useCallback(() => {
    setModalVisible(false)
  }, [])

  const renderSubmittedPhotoItem = useCallback(
    ({ item }: { item: AttachmentFile }) => (
      <PressableOpacity onPress={() => onPressPhoto(item.uri)}>
        <View style={styles.imageContainer}>
          <Image source={{ uri: item.uri }} style={styles.image} />
        </View>
      </PressableOpacity>
    ),
    [onPressPhoto, styles.image, styles.imageContainer]
  )

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps={'handled'}
      >
        <View style={[styles.sectionContainer, styles.summaryContainer]}>
          <Text style={styles.summaryText}>{occurrence?.summary}</Text>
        </View>
        <View style={[styles.sectionContainer]}>
          <Text style={[styles.titleText, styles.descriptionLabel]}>
            {t('description')}
          </Text>
          <View style={styles.descriptionContainer}>
            <ParsedText style={styles.descriptionText} parse={parsePatterns}>
              {occurrence?.description}
            </ParsedText>
          </View>
        </View>
        <View style={[styles.sectionContainer, styles.taskDataContainer]}>
          <View style={styles.assignedToContainer}>
            <Text style={styles.assignedToText}>{t('assignedTo')}</Text>
            <View style={styles.rowItemValueTextContainer}>
              <View style={styles.userAvatarContainer}>
                <UserAvatar user={task?.assignedTo} size={'small'} />
                {task?.assignedTo ? (
                  <Text style={[styles.rowItemValueText, styles.username]}>
                    {task?.assignedTo.firstName} {task?.assignedTo.lastName}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>

          <View style={styles.statusContainer}>
            <Text style={styles.titleText}>{t('status')}</Text>
            <View style={styles.rowItemValueTextContainer}>
              <Text style={styles.rowItemValueText}>
                {taskStatus.toUpperCase()}
              </Text>
            </View>
          </View>
          <View style={styles.dueDateContainer}>
            <Text style={styles.titleText}>{t('dueOn')}</Text>
            <View style={styles.rowItemValueTextContainer}>
              <Text style={styles.rowItemValueText}>
                {moment(occurrence?.dueDate).format('Do MMMM')}
              </Text>
            </View>
          </View>
        </View>

        {occurrence?.photoRequired ? (
          <View style={[styles.sectionContainer]}>
            <Text style={styles.photoRequiredText}>
              {t('photosRequiredLabel')}
            </Text>
            {isCompleted ? (
              <FlatList
                data={submittedPhotos}
                horizontal
                showsHorizontalScrollIndicator
                renderItem={renderSubmittedPhotoItem}
              />
            ) : (
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={photos}
                showsHorizontalScrollIndicator={true}
                horizontal
                renderItem={({ item, index }) => (
                  <AttachmentPreview
                    key={index}
                    attachment={item}
                    isUploading={isLoading}
                    onPressRemove={() => onPressRemovePhoto(item)}
                    onPressEdit={() => onPressEditPhoto(item)}
                    imageStyle={styles.image}
                  />
                )}
                contentContainerStyle={
                  styles.attachmentPreviewRowContentContainer
                }
                ListFooterComponent={() =>
                  isCompleted ? null : (
                    <PressableOpacity
                      onPress={onPressAddPhoto}
                      disabled={!canUploadPhotos}
                    >
                      <View
                        style={[
                          styles.uploadPhotoContainer,
                          {
                            borderColor: !canUploadPhotos
                              ? theme.colors.grey3
                              : theme.colors.primary,
                          },
                        ]}
                      >
                        {!canUploadPhotos ? (
                          <Text>{t('empty')}</Text>
                        ) : (
                          <Icon
                            name="plus"
                            color={theme.colors.primary}
                            type="font-awesome-5"
                            solid
                          />
                        )}
                      </View>
                    </PressableOpacity>
                  )
                }
              />
            )}
          </View>
        ) : null}
      </ScrollView>
      <SafeAreaView edges={['left', 'right', 'bottom']}>
        {isApprover && !isCompleted ? (
          <View>
            <View style={styles.bottomButton}>
              <RoundedButton
                title={t('reassign')}
                onPress={onReassignTask}
                loading={isLoading}
              />
            </View>
            <View style={styles.bottomButton}>
              <RoundedButton
                title={t('delete')}
                onPress={onPressDeleteTask}
                danger
                loading={isLoading}
              />
            </View>
          </View>
        ) : isAssignee ? (
          <View style={styles.assigneeButtonContainer}>
            <RoundedButton
              title={isCompleted ? t('completed') : t('markAsDone')}
              onPress={onPressCompleteTask}
              danger={isCompleted}
              disabled={isCompleted || isSubmitDisabled}
              loading={isLoading}
            />
          </View>
        ) : null}
        <RecurrentTaskDeleteModal
          visible={modalVisible}
          onConfirm={onConfirmDeleteRecurringTask}
          onClose={onCloseModal}
        />
      </SafeAreaView>
    </>
  )
}

function RecurrentTaskDeleteModal({
  visible,
  onConfirm,
  onClose,
}: {
  visible: boolean
  onConfirm: (updateType: RecurrentTaskUpdateType) => void
  onClose: () => void
}) {
  const styles = useStyles()
  const [updateType, setUpdateType] = useState<RecurrentTaskUpdateType>('THIS')
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView
          contentContainerStyle={styles.overlay}
          scrollEnabled={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.modalContainer}>
            <View style={styles.titleRow}>
              <Text style={styles.title}>
                {t('deletingRecurringTaskTitle')}
              </Text>
              <Text>{t('deletingRecurringTaskDescription')}</Text>
            </View>
            <View style={styles.inputSectionContainer}>
              <UpdateTypeChooser
                title={t('delete')}
                value={updateType}
                setValue={setUpdateType}
              />
            </View>
            <View style={styles.buttonsSectionContainer}>
              <Button
                title={t('cancel')}
                type={'outline'}
                onPress={onClose}
                containerStyle={styles.buttonContainer}
                buttonStyle={[styles.button, styles.cancelButton]}
              />
              <Button
                title={t('confirm')}
                onPress={() => onConfirm(updateType)}
                containerStyle={styles.buttonContainer}
                buttonStyle={styles.button}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flexGrow: 1,
    flexDirection: 'column',
    paddingHorizontal: 10,
  },
  titleText: {
    fontSize: 16,
  },
  sectionContainer: {
    marginTop: 15,
  },
  attachmentPreviewRowContentContainer: {
    alignItems: 'center',
  },
  rowItemValueTextContainer: {
    height: ROW_ITEM_VALUE_HEIGHT,
    justifyContent: 'center',
  },
  rowItemValueText: {
    textAlignVertical: 'center',
    fontWeight: 'bold',
  },
  overlay: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getDarkShadeColor(0.5),
  },
  modalContainer: {
    width: 350,
    padding: 20,
    backgroundColor: theme.colors.grey7,
    borderRadius: 10,
  },
  titleRow: { marginBottom: 5 },
  title: {
    fontSize: 18,
    marginBottom: 10,
  },
  buttonsSectionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 5,
  },
  buttonContainer: { borderRadius: 20 },
  button: { borderRadius: 20, width: 100 },
  cancelButton: { borderWidth: 0 },
  inputSectionContainer: {
    marginVertical: 10,
    borderRadius: 25,
    overflow: 'hidden',
  },
  image: {
    height: PHOTO_PREVIEW_SIZE,
    width: PHOTO_PREVIEW_SIZE,
    borderRadius: 10,
  },
  imageContainer: { padding: 5 },
  summaryContainer: { minHeight: 50 },
  summaryText: { fontSize: 22, fontWeight: 'bold' },
  descriptionLabel: { fontWeight: 'bold' },
  descriptionContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
    padding: 10,
    borderRadius: 20,
    minHeight: 100,
    marginTop: 5,
  },
  taskDataContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
    paddingTop: 10,
    borderRadius: 20,
  },
  assignedToContainer: { alignItems: 'center' },
  assignedToText: { fontSize: 16 },
  userAvatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  username: { marginLeft: 5 },
  statusContainer: {
    alignItems: 'center',
  },
  dueDateContainer: { alignItems: 'center' },
  photoRequiredText: { fontSize: 16, marginBottom: 10 },
  uploadPhotoContainer: {
    height: PHOTO_PREVIEW_SIZE,
    width: PHOTO_PREVIEW_SIZE,
    borderWidth: 1,

    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomButton: { marginBottom: 10, paddingHorizontal: 10 },
  assigneeButtonContainer: { padding: 20 },
  keyboardAvoidingView: { flex: 1 },
  descriptionText: { color: theme.colors.black },
}))
